<template>
  <v-card flat>
    <v-card-text>
      <!-- Реестр залогов - детали -->
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <!-- Если есть отфильтрованные записи - показываем оранжевый восклицательный знак -->
                <v-icon 
                  v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.filter_stats && clientCheck.pledge_registry_data.filter_stats.filtered_out > 0"
                  color="orange"
                >
                  mdi-alert
                </v-icon>
                <v-icon v-else :color="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'grey' : getScoreColor(clientCheck.pledge_score)">
                  {{ clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.pledge_score) }}
              </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.pledge_registry') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
              <v-chip
                    x-small 
                    :color="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'grey' : getScoreColor(clientCheck.pledge_score)"
                    dark
              >
                {{ clientCheck.pledge_score }}
              </v-chip>
            </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Статистика фильтрации -->
            <v-alert 
              v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.filter_stats"
              type="info" 
              text 
              dense 
              class="mb-3"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  {{ $t('parser_api.showing_relevant') }}: 
                  <strong>{{ clientCheck.pledge_registry_data.filtered_count || 0 }}</strong> 
                  {{ $t('parser_api.from') }} 
                  <strong>{{ clientCheck.pledge_registry_data.original_count || 0 }}</strong>
                </span>
                <span v-if="clientCheck.pledge_registry_data.filter_stats.filtered_out > 0" class="text-caption">
                  ({{ clientCheck.pledge_registry_data.filter_stats.filtered_out }} {{ $t('parser_api.filtered_records') }})
                </span>
              </div>
            </v-alert>
            
            <!-- Релевантные записи (прошедшие фильтр) -->
            <div v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.records && clientCheck.pledge_registry_data.records.length > 0">
              <h4 class="mb-2">{{ $t('parser_api.relevant_records') }}</h4>
              <v-data-table
                :headers="pledgeRelevantHeaders"
                :items="clientCheck.pledge_registry_data.records"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td>{{ getPledgeDate(item) }}</td>
                    <td>{{ getPledgeSubject(item) }}</td>
                    <td>{{ getPledgor(item) }}</td>
                    <td>{{ getPledgorBirth(item) }}</td>
                    <td>{{ getPledgee(item) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Отфильтрованные записи (низкая уверенность) -->
            <div v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.filtered_out_records && clientCheck.pledge_registry_data.filtered_out_records.length > 0" :class="clientCheck.pledge_registry_data.records && clientCheck.pledge_registry_data.records.length > 0 ? 'mt-4' : ''">
              <v-alert type="warning" text dense class="mb-2">
                <div>
                  <strong>{{ $t('parser_api.filtered_out_records_title') }}</strong>
                  <div class="text-caption">
                    {{ $t('parser_api.filtered_out_records_description') }}
                  </div>
                </div>
              </v-alert>
              
              <v-data-table
                :headers="pledgeFilteredHeaders"
                :items="clientCheck.pledge_registry_data.filtered_out_records"
                :items-per-page="5"
                class="elevation-1"
                :class="$vuetify.theme.dark ? 'grey darken-3' : 'orange lighten-5'"
              >
                <template v-slot:item="{ item }">
                  <tr :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'">
                    <td>{{ getPledgeDate(item) }}</td>
                    <td>{{ item.number || '-' }}</td>
                    <td>{{ getPledgeSubject(item) }}</td>
                    <td>{{ getPledgor(item) }}</td>
                    <td>{{ getPledgorBirth(item) }}</td>
                    <td>{{ getPledgee(item) }}</td>
                    <td>{{ getPledgeHistory(item) }}</td>
                    <td>
                      <v-chip x-small outlined color="orange">
                        {{ Math.round(item._confidence) }}%
                      </v-chip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Ничего не найдено (только если нет отфильтрованных) -->
            <v-alert 
              v-if="(!clientCheck.pledge_registry_data || !clientCheck.pledge_registry_data.records || clientCheck.pledge_registry_data.records.length === 0) && (!clientCheck.pledge_registry_data || !clientCheck.pledge_registry_data.filtered_out_records || clientCheck.pledge_registry_data.filtered_out_records.length === 0)" 
              type="info" 
              text
            >
              {{ $t('parser_api.no_records') }}
            </v-alert>
            
            <!-- Skipped из-за ошибки API -->
            <v-alert v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped" 
                     type="info" 
                     text 
                     class="mt-2">
              {{ $t('parser_api.check_skipped_api_error') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Арбитражные дела - детали -->
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <!-- Если есть отфильтрованные записи - показываем оранжевый восклицательный знак -->
                <v-icon 
                  v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.filter_stats && clientCheck.arbitr_cases_data.filter_stats.filtered_out > 0"
                  color="orange"
                >
                  mdi-alert
                </v-icon>
                <v-icon v-else :color="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'grey' : getScoreColor(clientCheck.arbitr_score)">
                  {{ clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.arbitr_score) }}
              </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.arbitr_cases') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
              <v-chip
                    x-small 
                    :color="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'grey' : getScoreColor(clientCheck.arbitr_score)"
                    dark
              >
                {{ clientCheck.arbitr_score }}
              </v-chip>
            </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Статистика фильтрации -->
            <v-alert 
              v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.filter_stats"
              type="info" 
              text 
              dense 
              class="mb-3"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  {{ $t('parser_api.showing_relevant') }}: 
                  <strong>{{ clientCheck.arbitr_cases_data.filtered_count || 0 }}</strong> 
                  {{ $t('parser_api.from') }} 
                  <strong>{{ clientCheck.arbitr_cases_data.original_count || 0 }}</strong>
                </span>
                <span v-if="clientCheck.arbitr_cases_data.filter_stats.filtered_out > 0" class="text-caption">
                  ({{ clientCheck.arbitr_cases_data.filter_stats.filtered_out }} {{ $t('parser_api.filtered_records') }})
                </span>
              </div>
            </v-alert>
            
            <!-- Релевантные записи (прошедшие фильтр) -->
            <div v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.Cases && clientCheck.arbitr_cases_data.Cases.length > 0">
              <h4 class="mb-2">{{ $t('parser_api.relevant_records') }}</h4>
              <v-data-table
                :headers="arbitrHeaders"
                :items="clientCheck.arbitr_cases_data.Cases"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td>{{ item.CaseNumber || '-' }}</td>
                    <td>{{ item.StartDate || '-' }}</td>
                    <td>{{ item.Court || '-' }}</td>
                    <td>{{ getCaseTypeText(item.CaseType) }}</td>
                    <td>
                      <v-chip small :color="getClientRole(item) === 'Ответчик' ? 'orange' : 'blue'" dark>
                        {{ getClientRole(item) }}
                      </v-chip>
                    </td>
                    <td>{{ getOpposingSide(item) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Отфильтрованные записи (низкая уверенность) -->
            <div v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.filtered_out_records && clientCheck.arbitr_cases_data.filtered_out_records.length > 0" :class="clientCheck.arbitr_cases_data.Cases && clientCheck.arbitr_cases_data.Cases.length > 0 ? 'mt-4' : ''">
              <v-alert type="warning" text dense class="mb-2">
                <div>
                  <strong>{{ $t('parser_api.filtered_out_records_title') }}</strong>
                  <div class="text-caption">
                    {{ $t('parser_api.filtered_out_records_description') }}
                  </div>
                </div>
              </v-alert>
              
              <v-data-table
                :headers="arbitrFilteredHeaders"
                :items="clientCheck.arbitr_cases_data.filtered_out_records"
                :items-per-page="5"
                class="elevation-1"
                :class="$vuetify.theme.dark ? 'grey darken-3' : 'orange lighten-5'"
              >
                <template v-slot:item="{ item }">
                  <tr :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'">
                    <td>{{ item.CaseNumber || '-' }}</td>
                    <td>{{ item.StartDate || '-' }}</td>
                    <td>{{ item.Court || '-' }}</td>
                    <td>{{ getCaseTypeText(item.CaseType) }}</td>
                    <td>
                      <v-chip x-small :color="getClientRole(item) === 'Ответчик' ? 'orange' : 'blue'" dark>
                        {{ getClientRole(item) }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip x-small outlined color="orange">
                        {{ Math.round(item._confidence) }}%
                      </v-chip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Ничего не найдено (только если нет отфильтрованных) -->
            <v-alert 
              v-if="(!clientCheck.arbitr_cases_data || !clientCheck.arbitr_cases_data.Cases || clientCheck.arbitr_cases_data.Cases.length === 0) && (!clientCheck.arbitr_cases_data || !clientCheck.arbitr_cases_data.filtered_out_records || clientCheck.arbitr_cases_data.filtered_out_records.length === 0)" 
              type="info" 
              text
            >
              {{ $t('parser_api.no_records') }}
            </v-alert>
            
            <!-- Skipped из-за ошибки API -->
            <v-alert v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped" 
                     type="info" 
                     text 
                     class="mt-2">
              {{ $t('parser_api.check_skipped_api_error') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Банкротство - детали -->
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <!-- Если есть отфильтрованные записи - показываем оранжевый восклицательный знак -->
                <v-icon 
                  v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.filter_stats && clientCheck.bankruptcy_data.filter_stats.filtered_out > 0"
                  color="orange"
                >
                  mdi-alert
                </v-icon>
                <v-icon v-else :color="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'grey' : getScoreColor(clientCheck.bankruptcy_score)">
                  {{ clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.bankruptcy_score) }}
              </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.bankruptcy') }}</div>
                <div class="text-caption d-flex align-center">
                  {{ $t('parser_api.score') }}: 
              <v-chip
                    x-small 
                    :color="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'grey' : getScoreColor(clientCheck.bankruptcy_score)" 
                    dark
                    class="ml-1"
              >
                {{ clientCheck.bankruptcy_score }}
              </v-chip>
            </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Статистика фильтрации -->
            <v-alert 
              v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.filter_stats"
              type="info" 
              text 
              dense 
              class="mb-3"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  {{ $t('parser_api.showing_relevant') }}: 
                  <strong>{{ clientCheck.bankruptcy_data.filtered_count || 0 }}</strong> 
                  {{ $t('parser_api.from') }} 
                  <strong>{{ clientCheck.bankruptcy_data.original_count || 0 }}</strong>
                </span>
                <span v-if="clientCheck.bankruptcy_data.filter_stats.filtered_out > 0" class="text-caption">
                  ({{ clientCheck.bankruptcy_data.filter_stats.filtered_out }} {{ $t('parser_api.filtered_records') }})
                </span>
              </div>
            </v-alert>
            
            <!-- Релевантные записи (прошедшие фильтр) -->
            <div v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.records && clientCheck.bankruptcy_data.records.length > 0">
              <h4 class="mb-2">{{ $t('parser_api.relevant_records') }}</h4>
              <v-data-table
                :headers="bankruptcyHeaders"
                :items="clientCheck.bankruptcy_data.records"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td>{{ item.caseNumber || '-' }}</td>
                    <td>{{ item.debtor || '-' }}</td>
                    <td>{{ item.publishDate || '-' }}</td>
                    <td>{{ item.status || '-' }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Отфильтрованные записи (низкая уверенность) -->
            <div v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.filtered_out_records && clientCheck.bankruptcy_data.filtered_out_records.length > 0" :class="clientCheck.bankruptcy_data.records && clientCheck.bankruptcy_data.records.length > 0 ? 'mt-4' : ''">
              <v-alert type="warning" text dense class="mb-2">
                <div>
                  <strong>{{ $t('parser_api.filtered_out_records_title') }}</strong>
                  <div class="text-caption">
                    {{ $t('parser_api.filtered_out_records_description') }}
                  </div>
                </div>
              </v-alert>
              
              <v-data-table
                :headers="bankruptcyFilteredHeaders"
                :items="clientCheck.bankruptcy_data.filtered_out_records"
                :items-per-page="5"
                class="elevation-1"
                :class="$vuetify.theme.dark ? 'grey darken-3' : 'orange lighten-5'"
              >
                <template v-slot:item="{ item }">
                  <tr :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'">
                    <td>{{ item.caseNumber || '-' }}</td>
                    <td>{{ item.debtor || '-' }}</td>
                    <td>{{ item.inn || '-' }}</td>
                    <td>{{ item.publishDate || '-' }}</td>
                    <td>{{ item.status || '-' }}</td>
                    <td>
                      <v-chip x-small outlined color="orange">
                        {{ Math.round(item._confidence) }}%
                      </v-chip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Ничего не найдено (только если нет отфильтрованных) -->
            <v-alert 
              v-if="(!clientCheck.bankruptcy_data || !clientCheck.bankruptcy_data.records || clientCheck.bankruptcy_data.records.length === 0) && (!clientCheck.bankruptcy_data || !clientCheck.bankruptcy_data.filtered_out_records || clientCheck.bankruptcy_data.filtered_out_records.length === 0)" 
              type="success" 
              text
            >
              {{ $t('parser_api.no_records') }}
            </v-alert>
            
            <!-- Skipped -->
            <v-alert v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped" 
                     type="info" 
                     text 
                     class="mt-2">
              {{ clientCheck.bankruptcy_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : clientCheck.bankruptcy_data.reason }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Черный список - детали -->
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="getScoreColor(clientCheck.blacklist_score)">
                  {{ getScoreIcon(clientCheck.blacklist_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.blacklist_check') }}</div>
                <div class="text-caption d-flex align-center">
                  {{ $t('parser_api.score') }}: 
                  <v-chip
                    x-small 
                    :color="getScoreColor(clientCheck.blacklist_score)" 
                    dark
                    class="ml-1"
                  >
                    {{ clientCheck.blacklist_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Клиент в черном списке -->
            <div v-if="clientCheck.blacklist_data && (clientCheck.blacklist_data.client_is_blacklisted || (clientCheck.blacklist_data.records && clientCheck.blacklist_data.records.length > 0))">
              <v-alert 
                type="error" 
                prominent 
                dense 
                class="mb-3"
              >
                <div class="d-flex align-center">
                  <v-icon color="error" class="mr-2" large>mdi-alert-octagon</v-icon>
                  <div>
                    <strong class="text-h6">{{ $t('parser_api.blacklist_client_in_list') }}</strong>
                    <div class="mt-1">{{ $t('parser_api.blacklist_client_in_list_description') }}</div>
                  </div>
                </div>
              </v-alert>

              <!-- Таблица найденных клиентов в черном списке -->
              <v-data-table
                :headers="blacklistHeaders"
                :items="clientCheck.blacklist_data.records"
                :items-per-page="5"
                class="elevation-1"
              >
                <template v-slot:item.fullname="{ item }">
                  {{ item.lastname }} {{ item.name }} {{ item.middlename }}
                </template>
                
                <template v-slot:item.birthday="{ item }">
                  {{ item.birthday || '-' }}
                </template>
                
                <template v-slot:item.inn="{ item }">
                  {{ item.inn || '-' }}
                </template>
                
                <template v-slot:item.passport="{ item }">
                  {{ item.passport || '-' }}
                </template>
                
                <template v-slot:item.driver_license="{ item }">
                  {{ item.driver_license || '-' }}
                </template>
                
                <template v-slot:item.problems_description="{ item }">
                  <div class="text-body-2" style="max-width: 400px; white-space: pre-wrap;">
                    {{ item.problems_description || '-' }}
                  </div>
                </template>
              </v-data-table>
            </div>
            
            <!-- Не найден в черном списке -->
            <v-alert v-else type="success" text>
              {{ $t('parser_api.blacklist_clean') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Проверка паспорта - детали -->
      <v-expansion-panels class="mb-4" v-if="clientCheck.passport_data">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="getPassportColor(clientCheck.passport_data.status)">
                  {{ getPassportIcon(clientCheck.passport_data.status) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.passport_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="getPassportColor(clientCheck.passport_data.status)" 
                    dark
                  >
                    {{ clientCheck.passport_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="clientCheck.passport_data.skipped">
              <v-alert type="info" text>
                {{ clientCheck.passport_data.reason === 'Ошибка API' ? $t('parser_api.passport_skipped_api_error') : (clientCheck.passport_data.reason || $t('parser_api.passport_skipped')) }}
              </v-alert>
              <div v-if="clientCheck.passport_data.error" class="mt-2 text-caption grey--text">
                Ошибка: {{ clientCheck.passport_data.error }}
              </div>
            </div>
            <div v-else>
              <v-simple-table>
                <tbody>
                  <tr>
                    <td><strong>{{ $t('parser_api.check_status') }}:</strong></td>
                    <td>
                      <v-chip
                        :color="getPassportColor(clientCheck.passport_data.status)"
                        small
                      >
                        {{ getPassportStatusText(clientCheck.passport_data.status) }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="clientCheck.passport_data.status === 'NOT_VALID'">
                    <td colspan="2">
                      <v-alert type="error" dense class="mt-2">
                        <strong>{{ $t('parser_api.passport_critical') }}</strong>
                      </v-alert>
                    </td>
                  </tr>
                  <tr v-if="clientCheck.passport_data.status === 'NOT_FOUND'">
                    <td colspan="2">
                      <v-alert type="error" dense class="mt-2">
                        <strong>{{ $t('parser_api.passport_not_found_warning') }}</strong>
                      </v-alert>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
            
            <!-- Debug info если есть note -->
            <v-alert v-if="clientCheck.passport_data.note" 
                     type="info" 
                     text 
                     class="mt-2">
              <small>{{ clientCheck.passport_data.note }}</small>
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Проверка ИНН -->
        <v-expansion-panel v-if="clientCheck.inn_data && Object.keys(clientCheck.inn_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="getInnColor(clientCheck.inn_data.status)">
                  {{ getInnIcon(clientCheck.inn_data.status) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.inn_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.inn_data && clientCheck.inn_data.skipped ? 'grey' : getScoreColor(clientCheck.inn_score)" 
                    dark
                  >
                    {{ clientCheck.inn_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <table class="details-table">
              <tr>
                <td><strong>{{ $t('parser_api.check_status') }}</strong></td>
                <td>
                  <v-chip 
                    small 
                    :color="getInnColor(clientCheck.inn_data.status)" 
                    dark
                  >
                    {{ getInnStatusText(clientCheck.inn_data.status) }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td><strong>{{ $t('parser_api.score') }}</strong></td>
                <td>
                  <v-chip 
                    small 
                    :color="getScoreColor(clientCheck.inn_score)" 
                    dark
                  >
                    {{ clientCheck.inn_score }}
                  </v-chip>
                </td>
              </tr>
            </table>

            <!-- Skipped из-за API ошибки -->
            <v-alert v-if="clientCheck.inn_data.skipped && clientCheck.inn_data.reason === 'Ошибка API'" 
                     type="info" 
                     text 
                     class="mt-3">
              {{ $t('parser_api.inn_skipped_api_error') }}
            </v-alert>

            <!-- Skipped из-за отсутствия данных -->
            <v-alert v-else-if="clientCheck.inn_data.skipped" 
                     type="info" 
                     text 
                     class="mt-3">
              {{ $t('parser_api.inn_skipped') }}: {{ clientCheck.inn_data.reason }}
            </v-alert>

            <!-- Найденный ИНН -->
            <div v-else-if="clientCheck.inn_data.found_inn">
              <v-divider class="my-3"></v-divider>
              <table class="details-table">
                <tr>
                  <td><strong>{{ $t('parser_api.inn_found') }}</strong></td>
                  <td>
                    <span class="text-h6 font-weight-bold">{{ clientCheck.inn_data.found_inn }}</span>
                  </td>
                </tr>
                
                <!-- Если есть ИНН в базе - показываем сравнение -->
                <tr v-if="clientCheck.inn_data.has_inn_in_db">
                  <td><strong>{{ $t('parser_api.inn_in_database') }}</strong></td>
                  <td>{{ clientCheck.inn_data.client_inn || $t('parser_api.not_specified') }}</td>
                </tr>
              </table>

              <!-- Статус совпадения -->
              <v-alert v-if="clientCheck.inn_data.status === 'MATCH'" 
                       type="success" 
                       text 
                       class="mt-3">
                <v-icon left small>mdi-check-circle</v-icon>
                {{ $t('parser_api.inn_match') }}
              </v-alert>

              <v-alert v-else-if="clientCheck.inn_data.status === 'MISMATCH'" 
                       type="warning" 
                       text 
                       class="mt-3">
                <v-icon left small>mdi-alert</v-icon>
                {{ $t('parser_api.inn_mismatch') }}
              </v-alert>

              <!-- Если нет ИНН в базе - предлагаем обновить -->
              <v-alert v-else-if="clientCheck.inn_data.status === 'FOUND_NEW'" 
                       type="info" 
                       text 
                       class="mt-3">
                <div>{{ $t('parser_api.inn_found_new') }}</div>
                <v-btn small color="primary" class="mt-2" @click="$emit('update-inn', clientCheck.inn_data.found_inn)">
                  {{ $t('parser_api.update_client_inn') }}
                </v-btn>
              </v-alert>
            </div>

            <!-- ИНН не найден -->
            <v-alert v-else-if="clientCheck.inn_data.status === 'NOT_FOUND'" 
                     type="info" 
                     text 
                     class="mt-3">
              {{ $t('parser_api.inn_not_found') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка алиментов -->
        <v-expansion-panel v-if="clientCheck.alimony_data && Object.keys(clientCheck.alimony_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="clientCheck.alimony_data && clientCheck.alimony_data.skipped ? 'grey' : getScoreColor(clientCheck.alimony_score)">
                  {{ clientCheck.alimony_data && clientCheck.alimony_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.alimony_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.alimony_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.alimony_data && clientCheck.alimony_data.skipped ? 'grey' : getScoreColor(clientCheck.alimony_score)" 
                    dark
                  >
                    {{ clientCheck.alimony_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Skipped из-за API ошибки -->
            <v-alert v-if="clientCheck.alimony_data.skipped && clientCheck.alimony_data.reason === 'Ошибка API'" 
                     type="info" 
                     text>
              {{ $t('parser_api.alimony_skipped_api_error') }}
            </v-alert>

            <!-- Skipped из-за отсутствия данных -->
            <v-alert v-else-if="clientCheck.alimony_data.skipped" 
                     type="info" 
                     text>
              {{ $t('parser_api.alimony_skipped') }}
            </v-alert>

            <!-- Записи найдены -->
            <div v-else-if="clientCheck.alimony_data.records && clientCheck.alimony_data.records.length > 0">
              <v-alert type="warning" text prominent class="mb-3">
                <v-icon left>mdi-alert</v-icon>
                {{ $t('parser_api.alimony_critical') }}
              </v-alert>
              
              <v-data-table
                :headers="alimonyHeaders"
                :items="clientCheck.alimony_data.records"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td>{{ item.fio || '-' }}</td>
                    <td>{{ item.birthDate || '-' }}</td>
                    <td>{{ item.ipNumber || '-' }}</td>
                    <td>{{ item.ipDate || '-' }}</td>
                    <td>{{ item.debtAmount || '-' }}</td>
                    <td>{{ item.bailiff || '-' }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>

            <!-- Записи не найдены -->
            <v-alert v-else type="success" text>
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('parser_api.no_records') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка ФССП -->
        <v-expansion-panel v-if="clientCheck.fssp_data && Object.keys(clientCheck.fssp_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <!-- Если есть отфильтрованные записи - показываем оранжевый восклицательный знак -->
                <v-icon 
                  v-if="clientCheck.fssp_data && clientCheck.fssp_data.filter_stats && clientCheck.fssp_data.filter_stats.filtered_out > 0"
                  color="orange"
                >
                  mdi-alert
                </v-icon>
                <v-icon v-else :color="clientCheck.fssp_data && clientCheck.fssp_data.skipped ? 'grey' : getScoreColor(clientCheck.fssp_score)">
                  {{ clientCheck.fssp_data && clientCheck.fssp_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.fssp_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.fssp_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.fssp_data && clientCheck.fssp_data.skipped ? 'grey' : getScoreColor(clientCheck.fssp_score)" 
                    dark
                  >
                    {{ clientCheck.fssp_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Skipped из-за API ошибки -->
            <v-alert v-if="clientCheck.fssp_data.skipped && clientCheck.fssp_data.reason === 'Ошибка API'" 
                     type="info" 
                     text>
              {{ $t('parser_api.fssp_skipped_api_error') }}
            </v-alert>

            <!-- Skipped из-за отсутствия данных -->
            <v-alert v-else-if="clientCheck.fssp_data.skipped" 
                     type="info" 
                     text>
              {{ $t('parser_api.fssp_skipped') }}
            </v-alert>

            <!-- Статистика фильтрации -->
            <v-alert 
              v-else-if="clientCheck.fssp_data.filter_stats"
              type="info" 
              text 
              dense 
              class="mb-3"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  {{ $t('parser_api.showing_relevant') }}: 
                  <strong>{{ clientCheck.fssp_data.filtered_count || 0 }}</strong> 
                  {{ $t('parser_api.from') }} 
                  <strong>{{ clientCheck.fssp_data.original_count || 0 }}</strong>
                </span>
                <span v-if="clientCheck.fssp_data.filter_stats.filtered_out > 0" class="text-caption">
                  ({{ clientCheck.fssp_data.filter_stats.filtered_out }} {{ $t('parser_api.filtered_records') }})
                </span>
              </div>
            </v-alert>

            <!-- Релевантные записи (прошедшие фильтр) -->
            <div v-if="clientCheck.fssp_data.result && clientCheck.fssp_data.result.length > 0">
              <v-alert type="warning" text prominent class="mb-3">
                {{ $t('parser_api.fssp_warning') }}
              </v-alert>
              
              <h4 class="mb-2">{{ $t('parser_api.relevant_records') }}</h4>
              <div class="text-body-2 mb-3">
                {{ $t('parser_api.fssp_records_found') }}: {{ clientCheck.fssp_data.result.length }}
                <span v-if="clientCheck.fssp_data.total_debt && clientCheck.fssp_data.total_debt > 0" class="ml-2 font-weight-bold error--text">
                  ({{ $t('parser_api.total_debt_amount') }}: {{ formatCurrency(clientCheck.fssp_data.total_debt) }})
                </span>
              </div>
              
              <v-expansion-panels flat>
                <v-expansion-panel
                  v-for="(record, index) in clientCheck.fssp_data.result"
                  :key="index"
                >
                  <v-expansion-panel-header>
                    ИП #{{ index + 1 }} - {{ record.process_title || 'Номер не указан' }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <table class="details-table">
                      <tr v-if="record.process_title">
                        <td><strong>{{ $t('parser_api.fssp_ip_number') }}</strong></td>
                        <td>{{ record.process_title }}</td>
                      </tr>
                      <tr v-if="record.process_date">
                        <td><strong>{{ $t('parser_api.fssp_ip_date') }}</strong></td>
                        <td>{{ record.process_date }}</td>
                      </tr>
                      <tr v-if="record.document_type">
                        <td><strong>{{ $t('parser_api.fssp_ip_subject') }}</strong></td>
                        <td>{{ record.document_type }}</td>
                      </tr>
                      <tr v-if="record.subjects && record.subjects.length > 0">
                        <td><strong>{{ $t('parser_api.fssp_ip_amount') }}</strong></td>
                        <td>
                          <div v-for="(subj, idx) in record.subjects" :key="idx">
                            {{ subj.title }}: {{ subj.sum }} ₽
                          </div>
                        </td>
                      </tr>
                      <tr v-if="record.department_title">
                        <td><strong>{{ $t('parser_api.fssp_ip_department') }}</strong></td>
                        <td>{{ record.department_title }}</td>
                      </tr>
                      <tr v-if="record.officer_name">
                        <td><strong>{{ $t('parser_api.fssp_ip_bailiff') }}</strong></td>
                        <td>{{ record.officer_name }}</td>
                      </tr>
                      <tr v-if="record.debtor_address">
                        <td><strong>Адрес должника</strong></td>
                        <td>{{ record.debtor_address }}</td>
                      </tr>
                    </table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            
            <!-- Отфильтрованные записи (низкая уверенность) -->
            <div v-if="clientCheck.fssp_data.filtered_out_records && clientCheck.fssp_data.filtered_out_records.length > 0" :class="clientCheck.fssp_data.result && clientCheck.fssp_data.result.length > 0 ? 'mt-4' : ''">
              <v-alert type="warning" text dense class="mb-2">
                <div>
                  <strong>{{ $t('parser_api.filtered_out_records_title') }}</strong>
                  <div class="text-caption">
                    {{ $t('parser_api.filtered_out_records_description') }}
                  </div>
                </div>
              </v-alert>
              
              <v-data-table
                :headers="fsspFilteredHeaders"
                :items="clientCheck.fssp_data.filtered_out_records"
                :items-per-page="5"
                class="elevation-1"
                :class="$vuetify.theme.dark ? 'grey darken-3' : 'orange lighten-5'"
              >
                <template v-slot:item="{ item }">
                  <tr :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'">
                    <td>{{ item.process_date || '-' }}</td>
                    <td>{{ item.process_title || '-' }}</td>
                    <td>{{ item.document_type || '-' }}</td>
                    <td>{{ item.department_title || '-' }}</td>
                    <td>
                      <v-chip x-small outlined color="orange">
                        {{ Math.round(item._confidence) }}%
                      </v-chip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>

            <!-- Записи не найдены (только если нет отфильтрованных) -->
            <v-alert 
              v-if="(!clientCheck.fssp_data.result || clientCheck.fssp_data.result.length === 0) && (!clientCheck.fssp_data.filtered_out_records || clientCheck.fssp_data.filtered_out_records.length === 0)"
              type="success" 
              text
            >
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('parser_api.no_records') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка ФССП розыск -->
        <v-expansion-panel v-if="clientCheck.fssp_search_data && Object.keys(clientCheck.fssp_search_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <!-- Если есть отфильтрованные записи - показываем оранжевый восклицательный знак -->
                <v-icon 
                  v-if="clientCheck.fssp_search_data && clientCheck.fssp_search_data.filter_stats && clientCheck.fssp_search_data.filter_stats.filtered_out > 0"
                  color="orange"
                >
                  mdi-alert
                </v-icon>
                <v-icon v-else :color="clientCheck.fssp_search_data && clientCheck.fssp_search_data.skipped ? 'grey' : getScoreColor(clientCheck.fssp_search_score)">
                  {{ clientCheck.fssp_search_data && clientCheck.fssp_search_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.fssp_search_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.fssp_search_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.fssp_search_data && clientCheck.fssp_search_data.skipped ? 'grey' : getScoreColor(clientCheck.fssp_search_score)" 
                    dark
                  >
                    {{ clientCheck.fssp_search_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Skipped -->
            <v-alert v-if="clientCheck.fssp_search_data.skipped" 
                     type="info" 
                     text>
              {{ clientCheck.fssp_search_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : (clientCheck.fssp_search_data.reason || $t('parser_api.passport_skipped')) }}
            </v-alert>

            <!-- Статистика фильтрации -->
            <v-alert 
              v-else-if="clientCheck.fssp_search_data.filter_stats"
              type="info" 
              text
            >
              <div><strong>{{ $t('parser_api.check_results') }}:</strong></div>
              <div>{{ $t('parser_api.total_found') }}: {{ clientCheck.fssp_search_data.original_count || 0 }}</div>
              <div>{{ $t('parser_api.relevant_records') }}: {{ clientCheck.fssp_search_data.filtered_count || 0 }}</div>
              <div v-if="clientCheck.fssp_search_data.filter_stats.filtered_out > 0" class="orange--text">
                {{ $t('parser_api.filtered_out') }}: {{ clientCheck.fssp_search_data.filter_stats.filtered_out }}
              </div>
            </v-alert>
            
            <!-- Релевантные записи (прошедшие фильтр) -->
            <div v-if="clientCheck.fssp_search_data.result && clientCheck.fssp_search_data.result.length > 0">
              <h4 class="mb-2">{{ $t('parser_api.relevant_records') }}</h4>
              <v-data-table
                :headers="fsspSearchHeaders"
                :items="clientCheck.fssp_search_data.result"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td>{{ item.search_title || '-' }}</td>
                    <td>{{ item.search_date || '-' }}</td>
                    <td>{{ item.search_category || '-' }}</td>
                    <td>{{ item.department_title || '-' }}</td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Отфильтрованные записи (низкая уверенность) -->
            <div v-if="clientCheck.fssp_search_data.filtered_out_records && clientCheck.fssp_search_data.filtered_out_records.length > 0" :class="clientCheck.fssp_search_data.result && clientCheck.fssp_search_data.result.length > 0 ? 'mt-4' : ''">
              <v-alert type="warning" text dense class="mb-2">
                <div>
                  <strong>{{ $t('parser_api.filtered_out_records_title') }}</strong>
                  <div class="text-caption">
                    {{ $t('parser_api.filtered_out_records_description') }}
                  </div>
                </div>
              </v-alert>
              
              <v-data-table
                :headers="fsspSearchFilteredHeaders"
                :items="clientCheck.fssp_search_data.filtered_out_records"
                :items-per-page="5"
                class="elevation-1"
                :class="$vuetify.theme.dark ? 'grey darken-3' : 'orange lighten-5'"
              >
                <template v-slot:item="{ item }">
                  <tr :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'">
                    <td>{{ item.search_title || '-' }}</td>
                    <td>{{ item.search_date || '-' }}</td>
                    <td>{{ item.search_category || '-' }}</td>
                    <td>{{ item.department_title || '-' }}</td>
                    <td>
                      <v-chip x-small outlined color="orange">
                        {{ Math.round(item._confidence) }}%
                      </v-chip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            
            <!-- Ничего не найдено -->
            <v-alert 
              v-if="(!clientCheck.fssp_search_data || !clientCheck.fssp_search_data.result || clientCheck.fssp_search_data.result.length === 0) && (!clientCheck.fssp_search_data || !clientCheck.fssp_search_data.filtered_out_records || clientCheck.fssp_search_data.filtered_out_records.length === 0)"
              type="success" 
              text
            >
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('parser_api.not_wanted') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка водительского удостоверения -->
        <v-expansion-panel v-if="clientCheck.driver_data && Object.keys(clientCheck.driver_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="clientCheck.driver_data && clientCheck.driver_data.skipped ? 'grey' : getScoreColor(clientCheck.driver_score)">
                  {{ clientCheck.driver_data && clientCheck.driver_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.driver_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.driver_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.driver_data && clientCheck.driver_data.skipped ? 'grey' : getScoreColor(clientCheck.driver_score)" 
                    dark
                  >
                    {{ clientCheck.driver_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Skipped из-за API ошибки -->
            <v-alert v-if="clientCheck.driver_data.skipped && clientCheck.driver_data.reason === 'Ошибка API'" 
                     type="info" 
                     text>
              {{ $t('parser_api.driver_skipped_api_error') }}
            </v-alert>

            <!-- Skipped из-за отсутствия данных -->
            <v-alert v-else-if="clientCheck.driver_data.skipped" 
                     type="info" 
                     text>
              {{ $t('parser_api.driver_skipped') }}
            </v-alert>

            <!-- ВУ не найдено -->
            <v-alert v-else-if="clientCheck.driver_data.status === 'NOT_FOUND'" 
                     type="info" 
                     text>
              {{ $t('parser_api.driver_not_found') }}
            </v-alert>

            <!-- ВУ найдено -->
            <div v-else-if="clientCheck.driver_data.document">
              <table class="details-table">
                <tr>
                  <td><strong>{{ $t('parser_api.driver_status') }}</strong></td>
                  <td>
                    <v-chip 
                      small 
                      :color="clientCheck.driver_data.status === 'Действует' ? 'success' : 'error'" 
                      dark
                    >
                      {{ clientCheck.driver_data.status }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('parser_api.score') }}</strong></td>
                  <td>
                    <v-chip 
                      small 
                      :color="getScoreColor(clientCheck.driver_score)" 
                      dark
                    >
                      {{ clientCheck.driver_score }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="clientCheck.driver_data.document.dob">
                  <td><strong>Дата рождения</strong></td>
                  <td>{{ clientCheck.driver_data.document.dob }}</td>
                </tr>
                <tr v-if="clientCheck.driver_data.document.categories">
                  <td><strong>{{ $t('parser_api.driver_categories') }}</strong></td>
                  <td>{{ clientCheck.driver_data.document.categories }}</td>
                </tr>
                <tr v-if="clientCheck.driver_data.document.date_from">
                  <td><strong>{{ $t('parser_api.driver_valid_from') }}</strong></td>
                  <td>{{ clientCheck.driver_data.document.date_from }}</td>
                </tr>
                <tr v-if="clientCheck.driver_data.document.date_to">
                  <td><strong>{{ $t('parser_api.driver_valid_to') }}</strong></td>
                  <td>{{ clientCheck.driver_data.document.date_to }}</td>
                </tr>
              </table>

              <!-- Комментарий -->
              <v-alert v-if="clientCheck.driver_data.document.comment" 
                       type="info" 
                       text 
                       class="mt-3">
                {{ clientCheck.driver_data.document.comment }}
              </v-alert>

              <!-- Критичное предупреждение -->
              <v-alert v-if="clientCheck.driver_data.status === 'Недействителен'" 
                       type="error" 
                       text 
                       prominent
                       class="mt-3">
                <v-icon left>mdi-alert-octagon</v-icon>
                {{ $t('parser_api.driver_critical') }}
              </v-alert>

              <!-- Информация о лишении -->
              <div v-if="clientCheck.driver_data.decision">
                <v-divider class="my-3"></v-divider>
                <v-alert type="error" text prominent>
                  <v-icon left>mdi-alert</v-icon>
                  {{ $t('parser_api.driver_deprivation') }}
                </v-alert>
                
                <table class="details-table mt-3">
                  <tr v-if="clientCheck.driver_data.decision.bplace">
                    <td><strong>{{ $t('parser_api.driver_deprivation_place') }}</strong></td>
                    <td>{{ clientCheck.driver_data.decision.bplace }}</td>
                  </tr>
                  <tr v-if="clientCheck.driver_data.decision.date">
                    <td><strong>{{ $t('parser_api.driver_deprivation_date') }}</strong></td>
                    <td>{{ clientCheck.driver_data.decision.date }}</td>
                  </tr>
                  <tr v-if="clientCheck.driver_data.decision.period">
                    <td><strong>{{ $t('parser_api.driver_deprivation_period') }}</strong></td>
                    <td>{{ clientCheck.driver_data.decision.period }} месяцев</td>
                  </tr>
                  <tr v-if="clientCheck.driver_data.decision.state">
                    <td><strong>{{ $t('parser_api.driver_deprivation_state') }}</strong></td>
                    <td>{{ clientCheck.driver_data.decision.state }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка КБМ -->
        <v-expansion-panel v-if="clientCheck.kbm_data && Object.keys(clientCheck.kbm_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="clientCheck.kbm_data && clientCheck.kbm_data.skipped ? 'grey' : getScoreColor(clientCheck.kbm_score)">
                  {{ clientCheck.kbm_data && clientCheck.kbm_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.kbm_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.kbm_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.kbm_data && clientCheck.kbm_data.skipped ? 'grey' : getScoreColor(clientCheck.kbm_score)" 
                    dark
                  >
                    {{ clientCheck.kbm_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Skipped из-за API ошибки -->
            <v-alert v-if="clientCheck.kbm_data.skipped && clientCheck.kbm_data.reason === 'Ошибка API или данные недостоверны'" 
                     type="info" 
                     text>
              {{ $t('parser_api.kbm_skipped_api_error') }}
            </v-alert>

            <!-- Skipped из-за отсутствия данных -->
            <v-alert v-else-if="clientCheck.kbm_data.skipped" 
                     type="info" 
                     text>
              {{ $t('parser_api.kbm_skipped') }}
            </v-alert>

            <!-- КБМ найден -->
            <div v-else-if="clientCheck.kbm_data.kbm">
              <table class="details-table">
                <tr>
                  <td><strong>{{ $t('parser_api.kbm_value') }}</strong></td>
                  <td>
                    <v-chip 
                      small 
                      :color="getKbmScoreColor(clientCheck.kbm_data.kbm)" 
                      dark
                    >
                      {{ clientCheck.kbm_data.kbm }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('parser_api.score') }}</strong></td>
                  <td>
                    <v-chip 
                      small 
                      :color="getScoreColor(clientCheck.kbm_score)" 
                      dark
                    >
                      {{ clientCheck.kbm_score }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="clientCheck.kbm_data.kbm_done !== undefined">
                  <td><strong>Достоверность данных</strong></td>
                  <td>{{ clientCheck.kbm_data.kbm_done === 1 ? 'Достоверно' : 'Недостоверно' }}</td>
                </tr>
              </table>

              <!-- Критичное предупреждение -->
              <v-alert v-if="clientCheck.kbm_score <= 30" 
                       type="error" 
                       text 
                       prominent
                       class="mt-3">
                <v-icon left>mdi-alert-octagon</v-icon>
                {{ $t('parser_api.kbm_critical') }}
              </v-alert>

              <!-- Страховые возмещения -->
              <div v-if="clientCheck.kbm_data.claims && clientCheck.kbm_data.claims.length > 0">
                <v-divider class="my-3"></v-divider>
                <div class="text-h6 mb-2">{{ $t('parser_api.kbm_claims') }}</div>
                <div class="text-body-2 mb-3">{{ $t('parser_api.kbm_claims_count') }}: {{ clientCheck.kbm_data.claims.length }}</div>
                
                <v-expansion-panels flat>
                  <v-expansion-panel
                    v-for="(claim, index) in clientCheck.kbm_data.claims"
                    :key="index"
                  >
                    <v-expansion-panel-header>
                      ДТП #{{ index + 1 }} - {{ claim.accident_date || 'Дата не указана' }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <table class="details-table">
                        <tr v-if="claim.accident_date">
                          <td><strong>{{ $t('parser_api.kbm_accident_date') }}</strong></td>
                          <td>{{ claim.accident_date }}</td>
                        </tr>
                        <tr v-if="claim.insurer_name">
                          <td><strong>{{ $t('parser_api.kbm_insurer') }}</strong></td>
                          <td>{{ claim.insurer_name }}</td>
                        </tr>
                        <tr v-if="claim.policy_serie || claim.policy_num">
                          <td><strong>{{ $t('parser_api.kbm_policy') }}</strong></td>
                          <td>{{ claim.policy_serie }} {{ claim.policy_num }}</td>
                        </tr>
                      </table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>

            <!-- КБМ не найден -->
            <v-alert v-else type="info" text>
              {{ $t('parser_api.kbm_not_found') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Проверка штрафов водителя -->
        <v-expansion-panel v-if="clientCheck.driver_fines_data && Object.keys(clientCheck.driver_fines_data).length > 0">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-icon :color="clientCheck.driver_fines_data && clientCheck.driver_fines_data.skipped ? 'grey' : getScoreColor(clientCheck.driver_fines_score)">
                  {{ clientCheck.driver_fines_data && clientCheck.driver_fines_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.driver_fines_score) }}
                </v-icon>
              </v-col>
              <v-col>
                <div class="text-h6">{{ $t('parser_api.driver_fines_check') }}</div>
                <div class="text-caption">
                  {{ $t('parser_api.score') }}: 
                  <v-chip 
                    x-small 
                    :color="clientCheck.driver_fines_data && clientCheck.driver_fines_data.skipped ? 'grey' : getScoreColor(clientCheck.driver_fines_score)" 
                    dark
                  >
                    {{ clientCheck.driver_fines_score }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Пропущено: причину показываем всегда, а не только для «Ошибка API» —
                 иначе на карточке остаётся глухое «Не проверялось». -->
            <v-alert v-if="clientCheck.driver_fines_data.skipped"
                     type="info"
                     text>
              {{ $t('parser_api.driver_fines_skipped') }}<template v-if="clientCheck.driver_fines_data.reason">: {{ clientCheck.driver_fines_data.reason }}</template>
            </v-alert>

            <!-- Штрафы найдены -->
            <div v-else-if="clientCheck.driver_fines_data.fines_count > 0">
              <v-alert type="warning" text prominent class="mb-3">
                <v-icon left>mdi-alert</v-icon>
                {{ $t('parser_api.driver_fines_found') }}: {{ clientCheck.driver_fines_data.fines_count }}
              </v-alert>
              
              <table class="details-table mb-3">
                <tr>
                  <td><strong>{{ $t('parser_api.fines_count') }}</strong></td>
                  <td>{{ clientCheck.driver_fines_data.fines_count }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('parser_api.total_amount') }}</strong></td>
                  <td class="error--text font-weight-bold">{{ formatCurrency(clientCheck.driver_fines_data.total_amount) }}</td>
                </tr>
                <tr v-if="hasExpiredFines">
                  <td><strong>{{ $t('parser_api.enforceable_total_amount') }}</strong></td>
                  <td>{{ formatCurrency(clientCheck.driver_fines_data.enforceable_total_amount) }}</td>
                </tr>
              </table>

              <!-- Список штрафов -->
              <div v-if="clientCheck.driver_fines_data.fines && clientCheck.driver_fines_data.fines.length > 0">
                <v-divider class="my-3"></v-divider>
                <div class="text-h6 mb-2">{{ $t('parser_api.fines_list') }}</div>
                
                <v-expansion-panels flat>
                  <v-expansion-panel
                    v-for="(fine, index) in clientCheck.driver_fines_data.fines"
                    :key="index"
                  >
                    <v-expansion-panel-header>
                      {{ $t('fines.fine') }} #{{ index + 1 }} - {{ formatCurrency(fine.Amount || fine.amount || 0) }}
                      <v-chip v-if="fine.expired" x-small color="grey" text-color="white" class="ml-2 flex-grow-0">
                        {{ $t('parser_api.fine_expired') }}
                      </v-chip>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <table class="details-table">
                        <tr v-if="fine.Date || fine.date">
                          <td><strong>{{ $t('parser_api.fine_date') }}</strong></td>
                          <td>{{ fine.Date || fine.date }}</td>
                        </tr>
                        <tr v-if="fine.Amount || fine.amount">
                          <td><strong>{{ $t('parser_api.fine_amount') }}</strong></td>
                          <td class="error--text font-weight-bold">{{ formatCurrency(fine.Amount || fine.amount) }}</td>
                        </tr>
                        <tr v-if="fine.Discount || fine.discount">
                          <td><strong>{{ $t('parser_api.fine_discount') }}</strong></td>
                          <td class="success--text">{{ formatCurrency(fine.Discount || fine.discount) }}</td>
                        </tr>
                        <tr v-if="fine.Uin || fine.uin">
                          <td><strong>{{ $t('parser_api.fine_uin') }}</strong></td>
                          <td>{{ fine.Uin || fine.uin }}</td>
                        </tr>
                        <tr v-if="fine.OffenseArticle || fine.offenseArticle">
                          <td><strong>{{ $t('parser_api.fine_article') }}</strong></td>
                          <td>{{ fine.OffenseArticle || fine.offenseArticle }}</td>
                        </tr>
                        <tr v-if="fine.OffensePlace || fine.offensePlace">
                          <td><strong>{{ $t('parser_api.fine_place') }}</strong></td>
                          <td>{{ fine.OffensePlace || fine.offensePlace }}</td>
                        </tr>
                        <tr v-if="fine.decisionDate">
                          <td><strong>{{ $t('parser_api.fine_decision_date') }}</strong></td>
                          <td>
                            {{ fine.decisionDate }}
                            <span v-if="fine.expired" class="grey--text">
                              — {{ $t('parser_api.fine_expired_hint') }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="fine.discountDate">
                          <td><strong>{{ $t('parser_api.fine_discount_date') }}</strong></td>
                          <td>{{ fine.discountDate }}</td>
                        </tr>
                        <tr v-if="fine.department">
                          <td><strong>{{ $t('parser_api.fine_department') }}</strong></td>
                          <td>{{ fine.department }}</td>
                        </tr>
                        <tr v-if="fine.payerName">
                          <td><strong>{{ $t('parser_api.fine_payer') }}</strong></td>
                          <td>{{ fine.payerName }}</td>
                        </tr>
                        <tr v-if="fine.payeeName">
                          <td><strong>{{ $t('parser_api.fine_payee') }}</strong></td>
                          <td>{{ fine.payeeName }}</td>
                        </tr>
                        <tr v-if="fine.purpose">
                          <td><strong>{{ $t('parser_api.fine_purpose') }}</strong></td>
                          <td>{{ fine.purpose }}</td>
                        </tr>
                      </table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>

            <!-- Штрафы не найдены -->
            <v-alert v-else type="success" text>
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('parser_api.no_fines') }}
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ClientCheckFullReport',
  props: {
    clientCheck: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      pledgeHeaders: [
        { text: 'Дата уведомления', value: 'noticeDate', sortable: false },
        { text: 'Предмет залога', value: 'pledgeSubject', sortable: false },
        { text: 'Залогодатель', value: 'pledgor', sortable: false },
        { text: 'Залогодержатель', value: 'pledgee', sortable: false }
      ],
      pledgeRelevantHeaders: [
        { text: 'Дата уведомления', value: 'noticeDate', sortable: false },
        { text: 'Предмет залога', value: 'pledgeSubject', sortable: false },
        { text: 'Залогодатель', value: 'pledgor', sortable: false },
        { text: 'Дата рождения', value: 'birth', sortable: false, width: '120px' },
        { text: 'Залогодержатель', value: 'pledgee', sortable: false }
      ],
      pledgeFilteredHeaders: [
        { text: 'Дата уведомления', value: 'noticeDate', sortable: false },
        { text: 'Номер записи', value: 'number', sortable: false },
        { text: 'Предмет залога', value: 'pledgeSubject', sortable: false },
        { text: 'Залогодатель', value: 'pledgor', sortable: false },
        { text: 'Дата рождения', value: 'birth', sortable: false, width: '120px' },
        { text: 'Залогодержатель', value: 'pledgee', sortable: false },
        { text: 'История', value: 'history', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      arbitrHeaders: [
        { text: 'Номер дела', value: 'CaseNumber', sortable: false },
        { text: 'Дата', value: 'StartDate', sortable: false },
        { text: 'Суд', value: 'Court', sortable: false },
        { text: 'Тип дела', value: 'CaseType', sortable: false },
        { text: 'Роль', value: 'Role', sortable: false },
        { text: 'Противная сторона', value: 'OpposingSide', sortable: false }
      ],
      arbitrFilteredHeaders: [
        { text: 'Номер дела', value: 'CaseNumber', sortable: false },
        { text: 'Дата', value: 'StartDate', sortable: false },
        { text: 'Суд', value: 'Court', sortable: false },
        { text: 'Тип дела', value: 'CaseType', sortable: false },
        { text: 'Роль', value: 'Role', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      bankruptcyHeaders: [
        { text: 'Номер дела', value: 'caseNumber', sortable: false },
        { text: 'Должник', value: 'debtor', sortable: false },
        { text: 'Дата публикации', value: 'publishDate', sortable: false },
        { text: 'Статус', value: 'status', sortable: false }
      ],
      bankruptcyFilteredHeaders: [
        { text: 'Номер дела', value: 'caseNumber', sortable: false },
        { text: 'Должник', value: 'debtor', sortable: false },
        { text: 'ИНН', value: 'inn', sortable: false, width: '150px' },
        { text: 'Дата публикации', value: 'publishDate', sortable: false },
        { text: 'Статус', value: 'status', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      alimonyFilteredHeaders: [
        { text: 'Дата возбуждения', value: 'date_start', sortable: false },
        { text: 'ФИО', value: 'name', sortable: false },
        { text: 'Дата рождения', value: 'birthday', sortable: false },
        { text: 'Адрес', value: 'address', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      fsspHeaders: [
        { text: 'Дата возбуждения', value: 'process_date', sortable: false },
        { text: 'Наименование', value: 'process_title', sortable: false },
        { text: 'Тип документа', value: 'document_type', sortable: false },
        { text: 'Отдел', value: 'department_title', sortable: false }
      ],
      fsspFilteredHeaders: [
        { text: 'Дата возбуждения', value: 'process_date', sortable: false },
        { text: 'Наименование', value: 'process_title', sortable: false },
        { text: 'Тип документа', value: 'document_type', sortable: false },
        { text: 'Отдел', value: 'department_title', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      fsspSearchHeaders: [
        { text: 'Номер розыскного дела', value: 'search_title', sortable: false },
        { text: 'Дата розыскного дела', value: 'search_date', sortable: false },
        { text: 'Категория', value: 'search_category', sortable: false },
        { text: 'Отдел ФССП', value: 'department_title', sortable: false }
      ],
      fsspSearchFilteredHeaders: [
        { text: 'Номер розыскного дела', value: 'search_title', sortable: false },
        { text: 'Дата розыскного дела', value: 'search_date', sortable: false },
        { text: 'Категория', value: 'search_category', sortable: false },
        { text: 'Отдел ФССП', value: 'department_title', sortable: false },
        { text: 'Совпадение', value: '_confidence', sortable: false, width: '120px' }
      ],
      blacklistHeaders: [
        { text: 'ФИО', value: 'fullname', sortable: false },
        { text: 'Дата рождения', value: 'birthday', sortable: false, width: '130px' },
        { text: 'ИНН', value: 'inn', sortable: false, width: '150px' },
        { text: 'Паспорт', value: 'passport', sortable: false, width: '150px' },
        { text: 'ВУ', value: 'driver_license', sortable: false, width: '150px' },
        { text: this.$t('parser_api.blacklist_problems_description'), value: 'problems_description', sortable: false }
      ],
      alimonyHeaders: [
        { text: 'ФИО должника', value: 'fio', sortable: false },
        { text: 'Дата рождения', value: 'birthDate', sortable: false },
        { text: 'Номер ИП', value: 'ipNumber', sortable: false },
        { text: 'Дата ИП', value: 'ipDate', sortable: false },
        { text: 'Сумма задолженности', value: 'debtAmount', sortable: false },
        { text: 'Судебный пристав', value: 'bailiff', sortable: false }
      ]
    }
  },
  computed: {
    // Строку «взыскиваемо» показываем только когда среди найденного есть просроченные
    // постановления: иначе она дублировала бы общий итог.
    hasExpiredFines() {
      const fines = (this.clientCheck.driver_fines_data && this.clientCheck.driver_fines_data.fines) || []
      return fines.some((fine) => fine.expired)
    }
  },
  methods: {
    getScoreColor(score) {
      if (score >= 75) return 'success'
      if (score >= 50) return 'warning'
      return 'error'
    },
    
    getScoreIcon(score) {
      if (score >= 75) return 'mdi-check-circle'
      if (score >= 50) return 'mdi-alert-circle'
      return 'mdi-close-circle'
    },
    
    formatCurrency(amount) {
      if (!amount || amount === 0) return '0 ₽'
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    },
    
    getPassportColor(status) {
      if (status === 'VALID') return 'success'
      if (status === 'NOT_VALID') return 'error'
      if (status === 'NOT_FOUND') return 'error'  // Тоже критично!
      return 'grey'
    },
    
    getPassportIcon(status) {
      if (status === 'VALID') return 'mdi-check-circle'
      if (status === 'NOT_VALID') return 'mdi-alert-octagon'
      if (status === 'NOT_FOUND') return 'mdi-alert-circle'  // Критично!
      return 'mdi-minus-circle'
    },
    
    getPassportStatusText(status) {
      if (status === 'VALID') return this.$t('parser_api.passport_valid')
      if (status === 'NOT_VALID') return this.$t('parser_api.passport_not_valid')
      if (status === 'NOT_FOUND') return this.$t('parser_api.passport_not_found')
      return status
    },
    
    getInnColor(status) {
      if (status === 'MATCH') return 'success'
      if (status === 'FOUND_NEW') return 'info'
      if (status === 'MISMATCH') return 'warning'
      if (status === 'NOT_FOUND') return 'grey'
      return 'grey'
    },
    
    getInnIcon(status) {
      if (status === 'MATCH') return 'mdi-check-circle'
      if (status === 'FOUND_NEW') return 'mdi-plus-circle'
      if (status === 'MISMATCH') return 'mdi-alert-circle'
      if (status === 'NOT_FOUND') return 'mdi-help-circle'
      return 'mdi-minus-circle'
    },
    
    getInnStatusText(status) {
      const statusMap = {
        'MATCH': this.$t('parser_api.inn_status_match'),
        'FOUND_NEW': this.$t('parser_api.inn_status_found_new'),
        'MISMATCH': this.$t('parser_api.inn_status_mismatch'),
        'NOT_FOUND': this.$t('parser_api.inn_status_not_found')
      }
      return statusMap[status] || this.$t('parser_api.inn_skipped')
    },
    
    getKbmScoreColor(kbm) {
      if (kbm >= 0.5 && kbm < 1.0) return 'success'  // Отлично
      if (kbm >= 1.0 && kbm < 1.5) return 'primary'  // Хорошо
      if (kbm >= 1.5 && kbm < 2.0) return 'warning'  // Средне
      if (kbm >= 2.0) return 'error'  // Плохо, критично
      return 'grey'  // Не определен
    },
    
    // Методы для арбитражных дел
    getCaseTypeText(caseType) {
      const types = {
        'А': 'Административное',
        'Б': 'Банкротное',
        'Г': 'Гражданское'
      }
      return types[caseType] || caseType
    },
    
    getClientRole(item) {
      if (!this.clientCheck || !this.clientCheck.client) return '-'
      
      const clientInn = this.normalizeInn(this.clientCheck.client.inn)
      
      // Проверяем, является ли клиент ответчиком
      if (item.Respondents && Array.isArray(item.Respondents)) {
        const isRespondent = item.Respondents.some(r => 
          (clientInn && this.normalizeInn(r.Inn) === clientInn) || 
          this.matchesClientName(r.Name)
        )
        if (isRespondent) return 'Ответчик'
      }
      
      // Проверяем, является ли клиент истцом
      if (item.Plaintiffs && Array.isArray(item.Plaintiffs)) {
        const isPlaintiff = item.Plaintiffs.some(p => 
          (clientInn && this.normalizeInn(p.Inn) === clientInn) || 
          this.matchesClientName(p.Name)
        )
        if (isPlaintiff) return 'Истец'
      }
      
      // Проверяем третьи лица
      if (item.Thirds && Array.isArray(item.Thirds)) {
        return 'Третье лицо'
      }
      
      // Проверяем иные лица
      if (item.Others && Array.isArray(item.Others)) {
        return 'Иное лицо'
      }
      
      return '-'
    },
    
    normalizeInn(inn) {
      if (!inn) return ''
      return String(inn).replace(/[\s-]/g, '').trim()
    },
    
    matchesClientName(name) {
      if (!name || !this.clientCheck || !this.clientCheck.client) return false
      
      const client = this.clientCheck.client
      if (!client.lastname || !client.name) return false
      
      const cleanName = name.replace(/^(ИП|Индивидуальный\s+предприниматель)\s+/i, '').toLowerCase()
      const clientName = `${client.lastname} ${client.name} ${client.middlename || ''}`.toLowerCase().trim()
      return cleanName.includes(clientName) || clientName.includes(cleanName)
    },
    
    getOpposingSide(item) {
      if (!item) return '-'
      
      // Определяем роль клиента
      const role = this.getClientRole(item)
      
      // Возвращаем противоположную сторону
      if (role === 'Ответчик' && item.Plaintiffs && item.Plaintiffs.length > 0) {
        return item.Plaintiffs[0].Name || '-'
      }
      
      if (role === 'Истец' && item.Respondents && item.Respondents.length > 0) {
        return item.Respondents[0].Name || '-'
      }
      
      return '-'
    },
    
    // Методы для реестра залогов
    getPledgeDate(item) {
      return item.register_date || item.noticeDate || item.regdate || '-'
    },
    
    getPledgeSubject(item) {
      // Пробуем разные варианты структуры данных
      if (item.objects && Array.isArray(item.objects) && item.objects.length > 0) {
        return item.objects[0]
      }
      if (item.pledgeSubject) {
        return item.pledgeSubject
      }
      if (item.object) {
        return item.object
      }
      // Проверяем, не исключена ли запись из реестра
      if (item.history && Array.isArray(item.history)) {
        const hasExclusion = item.history.some(h => h.type === 'Исключение' || h.type === 'Исключение по решению суда')
        if (hasExclusion) {
          return '(запись исключена из реестра)'
        }
      }
      return '-'
    },
    
    getPledgor(item) {
      // Пробуем разные варианты структуры данных
      if (item.pledgors && Array.isArray(item.pledgors) && item.pledgors.length > 0) {
        return item.pledgors[0].name || '-'
      }
      if (item.pledgor) {
        return item.pledgor
      }
      // Проверяем, не исключена ли запись из реестра
      if (item.history && Array.isArray(item.history)) {
        const hasExclusion = item.history.some(h => h.type === 'Исключение' || h.type === 'Исключение по решению суда')
        if (hasExclusion) {
          return '(запись исключена из реестра)'
        }
      }
      return '-'
    },
    
    getPledgorBirth(item) {
      // Пробуем разные варианты структуры данных
      if (item.pledgors && Array.isArray(item.pledgors) && item.pledgors.length > 0) {
        const pledgor = item.pledgors[0]
        return pledgor.birth || pledgor.birthday || '-'
      }
      if (item.birth || item.birthday) {
        return item.birth || item.birthday
      }
      // Проверяем, не исключена ли запись из реестра
      if (item.history && Array.isArray(item.history)) {
        const hasExclusion = item.history.some(h => h.type === 'Исключение' || h.type === 'Исключение по решению суда')
        if (hasExclusion) {
          return '(запись исключена из реестра)'
        }
      }
      return '-'
    },
    
    getPledgee(item) {
      // Пробуем разные варианты структуры данных
      if (item.pledgees && Array.isArray(item.pledgees) && item.pledgees.length > 0) {
        return item.pledgees[0].name || '-'
      }
      if (item.pledgee) {
        return item.pledgee
      }
      // Проверяем, не исключена ли запись из реестра
      if (item.history && Array.isArray(item.history)) {
        const hasExclusion = item.history.some(h => h.type === 'Исключение' || h.type === 'Исключение по решению суда')
        if (hasExclusion) {
          return '(запись исключена из реестра)'
        }
      }
      return '-'
    },
    
    getPledgeHistory(item) {
      if (!item.history || !Array.isArray(item.history)) {
        return '-'
      }
      // Показываем последнее событие
      const lastEvent = item.history[item.history.length - 1]
      return `${lastEvent.type} (${lastEvent.date})`
    }
  }
}
</script>

<style scoped>
</style>

